import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  Avatar,
} from "@mui/material";
import ComputerIcon from "@mui/icons-material/Computer";
import CloudIcon from "@mui/icons-material/Cloud";
import BarChartIcon from "@mui/icons-material/BarChart";
import FavoriteIcon from "@mui/icons-material/Favorite";
import WaterIcon from "@mui/icons-material/Water";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// ─── Data ───────────────────────────────────────────────────────────────────

const consultingServices = [
  {
    icon: <ComputerIcon sx={{ fontSize: 36 }} />,
    title: "IT Consulting",
    subtitle: "Strategic Technology Advisory",
    description:
      "We partner with healthcare organizations to modernize their technology strategy. From system audits to digital transformation roadmaps, we bring deep healthcare IT expertise to every engagement.",
    features: [
      "Healthcare IT strategy & roadmapping",
      "EHR/EMR system integration",
      "Technology stack evaluation",
      "IT governance & compliance (HIPAA, HL7, FHIR)",
      "Vendor selection & management",
      "IT project management",
    ],
    color: "#1a237e",
    gradient: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
    tag: "Advisory",
  },
  {
    icon: <CloudIcon sx={{ fontSize: 36 }} />,
    title: "Cloud Platform Design & Architecture",
    subtitle: "Scalable, Secure Cloud Solutions",
    description:
      "We design and implement enterprise-grade cloud infrastructure tailored for healthcare. Our AWS-certified architects deliver HIPAA-compliant, high-availability platforms built to scale with your organization.",
    features: [
      "AWS architecture design & implementation",
      "HIPAA-compliant cloud infrastructure",
      "Microservices & containerized deployments (EKS, Docker)",
      "CI/CD pipeline automation",
      "Disaster recovery & business continuity",
      "Cloud cost optimization",
    ],
    color: "#00897b",
    gradient: "linear-gradient(135deg, #00897b 0%, #00acc1 100%)",
    tag: "Infrastructure",
  },
  {
    icon: <BarChartIcon sx={{ fontSize: 36 }} />,
    title: "Data Analytics",
    subtitle: "Turning Health Data into Insight",
    description:
      "We help healthcare organizations unlock the value of their data. From real-world evidence to operational dashboards, we build analytics pipelines that drive smarter clinical and business decisions.",
    features: [
      "Healthcare data warehousing & ETL pipelines",
      "Real-world evidence (RWE) analytics",
      "Patient outcomes & population health analysis",
      "Operational & financial dashboards",
      "Predictive modeling & ML solutions",
      "Regulatory-grade data reporting",
    ],
    color: "#f9a825",
    gradient: "linear-gradient(135deg, #f9a825 0%, #fb8c00 100%)",
    tag: "Analytics",
  },
];

const digitalPlatforms = [
  {
    icon: <FavoriteIcon sx={{ fontSize: 32 }} />,
    name: "AmyloidConnect",
    tagline: "Amyloidosis Clinical Trials Platform",
    status: "Live",
    statusColor: "#00897b",
    description:
      "A comprehensive digital health platform connecting amyloidosis patients with clinical trials, specialized providers, and a global care community.",
    highlights: [
      "Clinical trial matching",
      "Patient & provider portals",
      "Secure health data sharing",
      "Community & peer support",
    ],
    url: "https://amyloidconnect.com",
    color: "#7b1fa2",
    gradient: "linear-gradient(135deg, #7b1fa2 0%, #1565c0 100%)",
  },
  {
    icon: <WaterIcon sx={{ fontSize: 32 }} />,
    name: "NephroTrax",
    tagline: "Chronic Kidney Disease Platform",
    status: "Coming Soon",
    statusColor: "#f9a825",
    description:
      "A disease-specific platform for the CKD community, featuring kidney education, dialysis center locator, clinical trial access, and patient-provider interaction tools.",
    highlights: [
      "CKD education center",
      "Dialysis locator",
      "Clinical trial matching",
      "Patient-provider interaction",
    ],
    url: null,
    color: "#00897b",
    gradient: "linear-gradient(135deg, #00897b 0%, #00acc1 100%)",
  },
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function ServiceCard({ service, index }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      elevation={hovered ? 12 : 2}
      sx={{
        height: "100%",
        borderRadius: 4,
        overflow: "hidden",
        transition: "all 0.35s ease",
        transform: hovered ? "translateY(-6px)" : "none",
        position: "relative",
        border: "1px solid",
        borderColor: hovered ? service.color : "rgba(0,0,0,0.07)",
      }}
    >
      {/* Top gradient bar */}
      <Box
        sx={{
          height: 6,
          background: service.gradient,
        }}
      />

      <CardContent sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              background: service.gradient,
              color: "white",
              flexShrink: 0,
            }}
          >
            {service.icon}
          </Avatar>
          <Box>
            <Chip
              label={service.tag}
              size="small"
              sx={{
                bgcolor: `${service.color}15`,
                color: service.color,
                fontWeight: 700,
                fontSize: "0.7rem",
                mb: 0.5,
                borderRadius: 1,
              }}
            />
            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ lineHeight: 1.2, color: "#1a1a2e" }}
            >
              {service.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: service.color, fontWeight: 600 }}
            >
              {service.subtitle}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.8, mb: 3 }}
        >
          {service.description}
        </Typography>

        <Divider sx={{ mb: 2.5 }} />

        {/* Features */}
        <Box>
          {service.features.map((feature, i) => (
            <Box
              key={i}
              sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 18,
                  color: service.color,
                  mt: 0.2,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function PlatformCard({ platform }) {
  const [hovered, setHovered] = useState(false);

  return (
    <Card
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      elevation={hovered ? 10 : 2}
      sx={{
        height: "100%",
        borderRadius: 4,
        overflow: "hidden",
        transition: "all 0.35s ease",
        transform: hovered ? "translateY(-6px)" : "none",
      }}
    >
      {/* Gradient header */}
      <Box
        sx={{
          background: platform.gradient,
          p: 4,
          color: "white",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decoration */}
        <Box
          sx={{
            position: "absolute",
            right: -20,
            top: -20,
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            right: 30,
            bottom: -30,
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Avatar
            sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 52, height: 52 }}
          >
            {platform.icon}
          </Avatar>
          <Chip
            label={platform.status}
            size="small"
            sx={{
              bgcolor: platform.statusColor,
              color: "white",
              fontWeight: 700,
              fontSize: "0.72rem",
            }}
          />
        </Box>

        <Typography variant="h4" fontWeight={800} gutterBottom>
          {platform.name}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          {platform.tagline}
        </Typography>
      </Box>

      <CardContent sx={{ p: 3 }}>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ lineHeight: 1.8, mb: 2.5 }}
        >
          {platform.description}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
          {platform.highlights.map((h, i) => (
            <Chip
              key={i}
              label={h}
              size="small"
              sx={{
                bgcolor: `${platform.color}12`,
                color: platform.color,
                fontWeight: 600,
                fontSize: "0.72rem",
              }}
            />
          ))}
        </Box>

        {platform.url ? (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            href={platform.url}
            target="_blank"
            sx={{
              background: platform.gradient,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: 3 },
            }}
          >
            Visit Platform
          </Button>
        ) : (
          <Button
            variant="outlined"
            disabled
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 700,
              borderColor: platform.color,
              color: platform.color,
              opacity: 0.6,
            }}
          >
            Coming Soon
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function ServicesPage() {
  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* ── HERO ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
          color: "white",
          py: { xs: 10, md: 14 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background decorations */}
        {[
          { width: 400, height: 400, top: -150, right: -100, opacity: 0.06 },
          { width: 250, height: 250, bottom: -80, left: 60, opacity: 0.05 },
          { width: 180, height: 180, top: 60, left: "40%", opacity: 0.04 },
        ].map((circle, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              width: circle.width,
              height: circle.height,
              borderRadius: "50%",
              bgcolor: "white",
              opacity: circle.opacity,
              top: circle.top,
              bottom: circle.bottom,
              left: circle.left,
              right: circle.right,
            }}
          />
        ))}

        <Container
          maxWidth="md"
          sx={{ textAlign: "center", position: "relative" }}
        >
          <Chip
            label="Our Services & Products"
            sx={{
              bgcolor: "rgba(255,255,255,0.15)",
              color: "white",
              fontWeight: 700,
              mb: 3,
              fontSize: "0.85rem",
              backdropFilter: "blur(4px)",
            }}
          />
          <Typography
            variant="h2"
            fontWeight={800}
            gutterBottom
            sx={{ fontSize: { xs: "2.2rem", md: "3rem" }, lineHeight: 1.2 }}
          >
            Healthcare Technology
            <Box component="span" sx={{ color: "#f9a825" }}>
              {" "}
              Tailored for Impact
            </Box>
          </Typography>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.88,
              maxWidth: 620,
              mx: "auto",
              lineHeight: 1.8,
              fontWeight: 400,
              mt: 2,
            }}
          >
            From enterprise IT consulting and cloud architecture to
            disease-specific digital health platforms — VeraInsight Healthcare
            delivers end-to-end technology solutions for modern healthcare.
          </Typography>
        </Container>
      </Box>

      {/* ── CONSULTING SERVICES ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: "center", mb: 7 }}>
          <Chip
            label="Consulting & Technology Services"
            sx={{
              bgcolor: "#1a237e15",
              color: "#1a237e",
              fontWeight: 700,
              mb: 2,
            }}
          />
          <Typography
            variant="h3"
            fontWeight={800}
            gutterBottom
            sx={{ color: "#1a1a2e" }}
          >
            What We Do
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 560, mx: "auto", lineHeight: 1.8 }}
          >
            We partner with hospitals, health systems, and healthcare startups
            to design, build, and optimize their technology infrastructure.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {consultingServices.map((service, i) => (
            <Grid item xs={12} md={4} key={i}>
              <ServiceCard service={service} index={i} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── DIVIDER ── */}
      <Box
        sx={{
          background:
            "linear-gradient(90deg, #1a237e 0%, #00897b 50%, #f9a825 100%)",
          height: 4,
          mx: { xs: 4, md: 12 },
          borderRadius: 2,
          opacity: 0.3,
        }}
      />

      {/* ── DIGITAL HEALTH PLATFORMS ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: "center", mb: 7 }}>
          <Chip
            label="Digital Health Platforms"
            sx={{
              bgcolor: "#00897b15",
              color: "#00897b",
              fontWeight: 700,
              mb: 2,
            }}
          />
          <Typography
            variant="h3"
            fontWeight={800}
            gutterBottom
            sx={{ color: "#1a1a2e" }}
          >
            Our Products
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 560, mx: "auto", lineHeight: 1.8 }}
          >
            Disease-specific platforms that connect patients with clinical
            trials, providers, and communities — making complex healthcare
            journeys simpler.
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {digitalPlatforms.map((platform, i) => (
            <Grid item xs={12} md={5} key={i}>
              <PlatformCard platform={platform} />
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── CTA ── */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
          py: { xs: 8, md: 10 },
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Ready to Work Together?
          </Typography>
          <Typography
            variant="body1"
            sx={{ opacity: 0.85, mb: 4, lineHeight: 1.8 }}
          >
            Whether you need a technology partner, a cloud architect, or a data
            analytics solution — let's talk about how VeraInsight Healthcare can
            help.
          </Typography>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            href="/contact"
            sx={{
              bgcolor: "#f9a825",
              color: "#1a1a2e",
              fontWeight: 800,
              borderRadius: 3,
              px: 5,
              py: 1.8,
              textTransform: "none",
              fontSize: "1rem",
              "&:hover": { bgcolor: "#fbc02d", boxShadow: 6 },
            }}
          >
            Get in Touch
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
